import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { LayersOutlined as LayersIcon, HelpOutline as HelpIcon, EditOutlined as CorrectIcon } from '@mui/icons-material';
import EmptyState from './EmptyState';
import CostTrackingHelpModal from './CostTrackingHelpModal';
import itemCostLayerService, { type ItemCostLayerView, type CostLayerRow } from '../../services/itemCostLayerService';
import { fromMinorUnitsOrNull, toMinorUnits } from '../../utils/money';
import { useAuth } from '../../context/AuthContext';

/**
 * COSTING.1a-web (E2) — the item-detail "Cost" section.
 *
 * Stat strip (current unit cost / last receipt / value on hand) + a FIFO
 * "Cost layers on hand" table, oldest first. Reads the thin ADMIN_ONLY endpoint
 * GET /admin/venue/:venueId/inventory/item/:itemId/cost-layers.
 *
 * INTERIM GATING: the parent renders this only for non-VENUE_USER roles (the
 * permissions-array mechanism lands in COSTING.1d). The endpoint is gated
 * server-side too, so this never fetches data the caller can't see.
 *
 * DORMANT venue → stat strip in "estimated" fallback mode + an info line; the
 * layers table is hidden (the FIFO engine hasn't been activated for the venue).
 *
 * All money arrives in minor units; convert at display with fromMinorUnits.
 */

interface Props {
  venueId: string;
  itemId: string;
  defaultCurrency?: string | null;
}

/** Source-type chip palette, aligned with MovementTypeBadge's colours.
 *  COSTING.1d — added the 1b/1c layer origins. */
const SOURCE_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  RECEIVE:           { label: 'Receipt',        color: '#1a7f37', bg: '#dafbe1' },
  OPENING:           { label: 'Opening',        color: '#0550ae', bg: '#e7f0fe' },
  ADJUSTMENT:        { label: 'Adjustment',     color: '#6e7781', bg: '#f6f8fa' },
  GENESIS:           { label: 'Genesis',        color: '#6e40c9', bg: '#ede9fe' },
  TRANSFER_IN:       { label: 'Transfer In',    color: '#0969da', bg: '#dbeafe' },
  PRODUCTION_OUTPUT: { label: 'Production',      color: '#1a7f37', bg: '#dafbe1' },
  COUNT_GAIN:        { label: 'Count Gain',     color: '#9a6700', bg: '#fef9c3' },
  REVERSAL:          { label: 'Reversal',       color: '#8250df', bg: '#fbefff' },
  PROVISIONAL:       { label: 'Provisional',    color: '#cf222e', bg: '#ffebe9' },
};

/** Cost-basis dot: only GENESIS / FALLBACK layers carry an estimate marker. */
const BASIS_DOT: Record<string, { color: string; tip: string }> = {
  GENESIS:  { color: '#6e40c9', tip: 'Genesis estimate — cost carried from the opening-balance snapshot when cost tracking was activated.' },
  FALLBACK: { color: '#9a6700', tip: 'Estimated cost — no purchase cost was recorded, so the last known cost was used.' },
};

const ItemCostSection: React.FC<Props> = ({ venueId, itemId, defaultCurrency }) => {
  const theme = useTheme();
  const [view, setView] = useState<ItemCostLayerView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const { isAdmin } = useAuth();
  // COSTING.1d — ADMIN-only "Correct cost" action (wired to the 1c endpoint).
  const [correctTarget, setCorrectTarget] = useState<CostLayerRow | null>(null);
  const [correctCost, setCorrectCost] = useState('');
  const [correctNotes, setCorrectNotes] = useState('');
  const [correcting, setCorrecting] = useState(false);
  const [correctError, setCorrectError] = useState<string | null>(null);

  const openCorrect = (row: CostLayerRow) => {
    setCorrectTarget(row);
    setCorrectCost(row.unitCost != null ? String(fromMinorUnitsOrNull(row.unitCost)) : '');
    setCorrectNotes('');
    setCorrectError(null);
  };
  const submitCorrect = async () => {
    if (!correctTarget) return;
    const major = parseFloat(correctCost);
    if (isNaN(major) || major < 0) { setCorrectError('Enter a valid cost.'); return; }
    if (!correctNotes.trim()) { setCorrectError('A note is required.'); return; }
    setCorrecting(true);
    setCorrectError(null);
    try {
      await itemCostLayerService.correctCostLayer(venueId, correctTarget.layerId, toMinorUnits(major), correctNotes.trim());
      setCorrectTarget(null);
      await load();
    } catch (err: any) {
      setCorrectError(err?.response?.data?.error?.message ?? 'Failed to correct cost.');
    } finally {
      setCorrecting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await itemCostLayerService.getItemCostLayers(venueId, itemId);
      setView(data);
    } catch {
      setError('Could not load cost layers.');
    } finally {
      setLoading(false);
    }
  }, [venueId, itemId]);

  useEffect(() => { void load(); }, [load]);

  // ── Formatters ────────────────────────────────────────────────────────────
  const fmtMoney = (minor?: number | null) => {
    const major = fromMinorUnitsOrNull(minor);
    return major != null
      ? `${defaultCurrency ? `${defaultCurrency} ` : ''}${major.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '—';
  };
  const fmtDay = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  const fmtDayShort = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  const fmtYear = (iso: string) => new Date(iso).toLocaleDateString(undefined, { year: 'numeric' });
  const fmtQty = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 3 });

  // ── Stat card ─────────────────────────────────────────────────────────────
  const StatCard: React.FC<{ label: string; value: React.ReactNode; caption?: React.ReactNode; captionDotted?: string }> = ({ label, value, caption, captionDotted }) => (
    <Paper
      variant="outlined"
      sx={{ flex: '1 1 160px', minWidth: 160, p: 2, borderRadius: 2, borderColor: alpha(theme.palette.divider, 0.5) }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5, lineHeight: 1.2 }}>{value}</Typography>
      {caption && (
        captionDotted ? (
          <Tooltip title={captionDotted} arrow>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-block', mt: 0.25, borderBottom: `1px dotted ${alpha(theme.palette.text.secondary, 0.6)}`, cursor: 'help' }}>
              {caption}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{caption}</Typography>
        )
      )}
    </Paper>
  );

  const header = (
    <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>Cost</Typography>
        <Typography variant="caption" color="text.secondary">
          The cost of stock on hand, oldest first
        </Typography>
      </Box>
      <Tooltip title="Where these numbers come from" arrow>
        <IconButton size="small" onClick={() => setHelpOpen(true)} sx={{ color: 'text.secondary' }}>
          <HelpIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // COST-GUIDE.1 — the shared cost guide replaces the former inline dialog, so
  // every cost surface opens the same explanation.
  const helpModal = <CostTrackingHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />;

  // COSTING.1d — ADMIN correct-cost dialog (mandatory notes).
  const correctDialog = (
    <Dialog open={correctTarget != null} onClose={correcting ? undefined : () => setCorrectTarget(null)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Correct cost</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set the correct unit cost for this layer. The change is recorded as a cost adjustment. If
          the layer has already been partly consumed, the correction is recorded against the consumed
          history without changing remaining stock.
        </Typography>
        {correctError && <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>{correctError}</Typography>}
        <TextField
          label={`Unit cost${defaultCurrency ? ` (${defaultCurrency})` : ''}`}
          type="number" size="small" fullWidth value={correctCost}
          onChange={e => setCorrectCost(e.target.value)}
          slotProps={{ htmlInput: { min: 0, step: 0.01 } }} sx={{ mb: 2 }}
        />
        <TextField
          label="Reason (required)" size="small" fullWidth multiline minRows={2}
          value={correctNotes} onChange={e => setCorrectNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setCorrectTarget(null)} disabled={correcting} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={submitCorrect} disabled={correcting} sx={{ textTransform: 'none', fontWeight: 600 }}>
          {correcting ? 'Saving…' : 'Save correction'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const wrap = (children: React.ReactNode) => (
    <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
      {header}
      {children}
      {helpModal}
      {correctDialog}
    </Paper>
  );

  if (loading) {
    return wrap(
      <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>,
    );
  }
  if (error || !view) {
    return wrap(
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">{error ?? 'Could not load cost layers.'}</Typography>
      </Box>,
    );
  }

  const { active, headUnitCost, fallbackUnitCost, lastReceipt, valueOnHand, totalRemainingUnits, uncostedUnits, layers } = view;

  // ── Stat strip ─────────────────────────────────────────────────────────────
  // Current unit cost: FIFO head layer, else balance lastCost ("estimated").
  const hasHead = headUnitCost != null;
  const currentValue = hasHead ? fmtMoney(headUnitCost) : fmtMoney(fallbackUnitCost);
  const currentCaption = hasHead ? 'next to be consumed' : 'estimated';
  const currentDotted = hasHead ? undefined : 'No cost layers on hand — this is the last known cost from the stock balance, not a FIFO layer.';

  const lastReceiptCaption = lastReceipt
    ? [fmtDay(lastReceipt.receivedDate), lastReceipt.supplierName].filter(Boolean).join(' · ')
    : undefined;

  const valueCaption = totalRemainingUnits > 0
    ? `${fmtQty(totalRemainingUnits)} unit${totalRemainingUnits === 1 ? '' : 's'}${uncostedUnits > 0 ? ` · ${fmtQty(uncostedUnits)} uncosted` : ''}`
    : undefined;

  const statStrip = (
    <Box sx={{ p: 3, pb: active && layers.length ? 2 : 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <StatCard label="Current unit cost" value={currentValue} caption={currentCaption} captionDotted={currentDotted} />
      <StatCard label="Last receipt cost" value={lastReceipt ? fmtMoney(lastReceipt.unitCost) : '—'} caption={lastReceiptCaption} />
      <StatCard label="Value on hand" value={fmtMoney(valueOnHand)} caption={valueCaption} />
    </Box>
  );

  // ── Dormant venue: stat strip (fallback) + info line, no layers table ───────
  if (!active) {
    return wrap(
      <>
        {statStrip}
        <Box sx={{ px: 3, pb: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Cost tracking is not yet active for this venue — figures above are estimated from stock balances.
          </Typography>
        </Box>
      </>,
    );
  }

  // ── Active venue: stat strip + FIFO layers table ────────────────────────────
  const headLayerId = layers.find(l => l.remainingQty > 0)?.layerId;

  const sourceChip = (l: CostLayerRow) => {
    const cfg = SOURCE_CONFIG[l.sourceType] ?? { label: l.sourceType, color: '#6e7781', bg: '#f6f8fa' };
    return (
      <Box>
        <Chip
          label={cfg.label}
          size="small"
          sx={{ fontWeight: 600, fontSize: '0.7rem', color: cfg.color, backgroundColor: cfg.bg, border: 'none', height: 22 }}
        />
        {l.lotNumber && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            Lot {l.lotNumber}
          </Typography>
        )}
      </Box>
    );
  };

  const admin = isAdmin();
  const HEADERS = ['Received', 'Source', 'Remaining / Original', 'Unit Cost', 'Value', ...(admin ? [''] : [])];

  return wrap(
    <>
      {statStrip}
      {layers.length === 0 ? (
        <EmptyState
          icon={LayersIcon}
          message="No cost layers yet…"
          hint="Cost layers appear as stock is received or opened. Received stock with no cost recorded stays uncosted until corrected."
        />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {HEADERS.map((h, i) => (
                  <TableCell key={h} align={i >= 3 ? 'right' : 'left'}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {layers.map(l => {
                const isHead = l.layerId === headLayerId;
                const basis = BASIS_DOT[l.costBasis];
                const zeroed = l.remainingQty <= 0;
                return (
                  <TableRow
                    key={l.layerId}
                    sx={{
                      opacity: zeroed ? 0.5 : 1,
                      backgroundColor: isHead ? alpha(theme.palette.primary.main, 0.04) : undefined,
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ lineHeight: 1.2 }}>{fmtDayShort(l.receivedDate)}</Typography>
                      <Typography variant="caption" color="text.secondary">{fmtYear(l.receivedDate)}</Typography>
                    </TableCell>
                    <TableCell>{sourceChip(l)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" component="span">{fmtQty(l.remainingQty)}</Typography>
                      <Typography variant="caption" color="text.secondary" component="span"> / {fmtQty(l.originalQty)}</Typography>
                      {isHead && (
                        <Tooltip title="Next to be consumed under FIFO" arrow>
                          <Box
                            component="span"
                            sx={{ ml: 1, px: 0.75, py: '1px', borderRadius: 1, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}
                          >
                            Next
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-end' }}>
                        {basis && (
                          <Tooltip title={basis.tip} arrow>
                            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: basis.color, cursor: 'help', flexShrink: 0 }} />
                          </Tooltip>
                        )}
                        {l.unitCost != null ? fmtMoney(l.unitCost) : <Typography variant="caption" color="text.secondary">uncosted</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{l.value != null ? fmtMoney(l.value) : '—'}</TableCell>
                    {admin && (
                      <TableCell align="right" sx={{ width: 40 }}>
                        {l.sourceType !== 'PROVISIONAL' && (
                          <Tooltip title="Correct cost…" arrow>
                            <IconButton size="small" onClick={() => openCorrect(l)} sx={{ color: 'text.disabled' }}>
                              <CorrectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>,
  );
};

export default ItemCostSection;
